import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useModuleAccess, moduleCodeForPath } from "@/hooks/useModuleAccess";
import { PermissionProvider } from "@/contexts/PermissionContext";
import { TenantFilterProvider } from "@/contexts/TenantFilterContext";
import { Loader2 } from "lucide-react";

// Eager load critical pages (login flow)
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";

// Lazy load all other pages for code splitting
import LandingPage from "./pages/LandingPage";
const Home = lazy(() => import("./pages/Home"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Works = lazy(() => import("./pages/Works"));
const Templates = lazy(() => import("./pages/Templates"));
const Inspections = lazy(() => import("./pages/Inspections"));
const NewInspection = lazy(() => import("./pages/NewInspection"));
const EditInspection = lazy(() => import("./pages/EditInspection"));
const InspectionDetail = lazy(() => import("./pages/InspectionDetail"));
const Tenants = lazy(() => import("./pages/Tenants"));
const Users = lazy(() => import("./pages/Users"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const VerifyInspection = lazy(() => import("./pages/VerifyInspection"));
const Registration = lazy(() => import("./pages/Registration"));
const ChecklistModule = lazy(() => import("./pages/ChecklistModule"));
const DashboardConfig = lazy(() => import("./pages/DashboardConfig"));
const Feedback = lazy(() => import("./pages/Feedback"));
const FeedbackList = lazy(() => import("./pages/FeedbackList"));
const DocumentForms = lazy(() => import("./pages/DocumentForms"));
const Workforce = lazy(() => import("./pages/Workforce"));
const Trainings = lazy(() => import("./pages/Trainings"));
const Permissions = lazy(() => import("./pages/Permissions"));
const Sectors = lazy(() => import("./pages/Sectors"));
const FvmMaterials = lazy(() => import("./pages/FvmMaterials"));
const SiteDiary = lazy(() => import("./pages/SiteDiary"));
const SiteDiaryPhotos = lazy(() => import("./pages/SiteDiaryPhotos"));
const SacPortal = lazy(() => import("./pages/sac/SacPortal"));
const SacNovoChamado = lazy(() => import("./pages/sac/SacNovoChamado"));
const SacNovoChamadoInterno = lazy(() => import("./pages/sac/SacNovoChamadoInterno"));
const SacPesquisa = lazy(() => import("./pages/sac/SacPesquisa"));
const SacInbox = lazy(() => import("./pages/sac/SacInbox"));
const SacChamadoDetalhe = lazy(() => import("./pages/sac/SacChamadoDetalhe"));
const SacDashboard = lazy(() => import("./pages/sac/SacDashboard"));
const SacWhatsappConfig = lazy(() => import("./pages/sac/SacWhatsappConfig"));
const SacProviders = lazy(() => import("./pages/sac/SacProviders"));
const MasterList = lazy(() => import("./pages/MasterList"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Projects = lazy(() => import("./pages/Projects"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const Equipment = lazy(() => import("./pages/Equipment"));
const Planejamento = lazy(() => import("./pages/Planejamento"));
const Empreendimento = lazy(() => import("./pages/Empreendimento"));
const WorkStructure = lazy(() => import("./pages/planning/WorkStructure"));
const Stock = lazy(() => import("./pages/Stock"));
const Purchases = lazy(() => import("./pages/Purchases"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Nr18Guide = lazy(() => import("./pages/Nr18Guide"));
const Chatbots = lazy(() => import("./pages/Chatbots"));
const ChatbotDetail = lazy(() => import("./pages/ChatbotDetail"));
const Traceability = lazy(() => import("./pages/Traceability"));
const ConcreteIndex = lazy(() => import("./pages/concrete/ConcreteIndex"));
const ConcreteSuppliers = lazy(() => import("./pages/concrete/ConcreteSuppliers"));
const ConcreteMixes = lazy(() => import("./pages/concrete/ConcreteMixes"));
const ConcreteLabs = lazy(() => import("./pages/concrete/ConcreteLabs"));
const StructuralElements = lazy(() => import("./pages/concrete/StructuralElements"));
const ConcretePoursList = lazy(() => import("./pages/concrete/ConcretePoursList"));
const ConcretePourForm = lazy(() => import("./pages/concrete/ConcretePourForm"));
const ConcreteSpecimens = lazy(() => import("./pages/concrete/ConcreteSpecimens"));
const ConcreteDashboard = lazy(() => import("./pages/concrete/ConcreteDashboard"));
const ThicknessDashboard = lazy(() => import("./pages/thickness/ThicknessDashboard"));
const ThicknessServices = lazy(() => import("./pages/thickness/ThicknessServices"));
const ThicknessEntry = lazy(() => import("./pages/thickness/ThicknessEntry"));

const queryClient = new QueryClient();

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children, requireAdmin = false, requireSuperAdmin = false }: { 
  children: React.ReactNode; 
  requireAdmin?: boolean;
  requireSuperAdmin?: boolean;
}) {
  const { user, loading, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const { canView, loading: accessLoading } = useModuleAccess();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/home" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/home" replace />;
  }

  // Granular permissions: non-admins need the module's "view" permission
  if (accessLoading) return <PageLoader />;
  if (!canView(moduleCodeForPath(location.pathname))) {
    return <Navigate to="/home" replace />;
  }

  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// Public Route wrapper (redirects to dashboard if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (user) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
}

// Layout wrapper for providers
function RootLayout() {
  return (
    <AuthProvider>
      <TenantFilterProvider>
        <PermissionProvider>
          <Outlet />
        </PermissionProvider>
      </TenantFilterProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/",
        element: <LandingPage />,
      },
      {
        path: "/home",
        element: <ProtectedRoute><Home /></ProtectedRoute>,
      },
      {
        path: "/login",
        element: <PublicRoute><Login /></PublicRoute>,
      },
      {
        path: "/install",
        element: <Suspense fallback={<PageLoader />}><InstallApp /></Suspense>,
      },
      {
        path: "/terms",
        element: <Suspense fallback={<PageLoader />}><Terms /></Suspense>,
      },
      {
        path: "/privacy",
        element: <Suspense fallback={<PageLoader />}><Privacy /></Suspense>,
      },
      {
        path: "/blog/guia-nr-18",
        element: <Suspense fallback={<PageLoader />}><Nr18Guide /></Suspense>,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        path: "/verificar/:codigo",
        element: <Suspense fallback={<PageLoader />}><VerifyInspection /></Suspense>,
      },
      // ====== Portal Público do Cliente (sem auth) — /portal/sac/* ======
      {
        path: "/portal/sac",
        element: <Suspense fallback={<PageLoader />}><SacPortal /></Suspense>,
      },
      {
        path: "/portal/sac/novo",
        element: <Suspense fallback={<PageLoader />}><SacNovoChamado /></Suspense>,
      },
      {
        path: "/portal/sac/pesquisa",
        element: <Suspense fallback={<PageLoader />}><SacPesquisa /></Suspense>,
      },
      // Redirects de compatibilidade (links antigos enviados ao cliente)
      { path: "/sac", element: <Navigate to="/sac/inbox" replace /> },
      { path: "/sac/portal", element: <Navigate to="/portal/sac" replace /> },
      { path: "/sac/pesquisa", element: <Navigate to="/portal/sac/pesquisa" replace /> },

      // ====== Painel Interno da Construtora (com auth) — /sac/* ======
      {
        path: "/sac/inbox",
        element: <ProtectedRoute><SacInbox /></ProtectedRoute>,
      },
      {
        path: "/sac/dashboard",
        element: <ProtectedRoute><SacDashboard /></ProtectedRoute>,
      },
      {
        path: "/sac/novo-interno",
        element: <ProtectedRoute><SacNovoChamadoInterno /></ProtectedRoute>,
      },
      {
        path: "/sac/chamado/:id",
        element: <ProtectedRoute><SacChamadoDetalhe /></ProtectedRoute>,
      },
      {
        path: "/sac/whatsapp",
        element: <ProtectedRoute><SacWhatsappConfig /></ProtectedRoute>,
      },
      // Aliases legados do admin
      { path: "/sac/admin", element: <Navigate to="/sac/inbox" replace /> },
      { path: "/sac/admin/dashboard", element: <Navigate to="/sac/dashboard" replace /> },
      { path: "/sac/admin/:id", element: <Navigate to="/sac/inbox" replace /> },
      { path: "/sac/config", element: <Navigate to="/sac/whatsapp" replace /> },
      {
        path: "/super-admin/sac-providers",
        element: <ProtectedRoute requireSuperAdmin><SacProviders /></ProtectedRoute>,
      },
      {
        path: "/dashboard",
        element: <ProtectedRoute><Dashboard /></ProtectedRoute>,
      },
      {
        path: "/cadastro",
        element: <ProtectedRoute requireAdmin><Registration /></ProtectedRoute>,
      },
      {
        path: "/registration",
        element: <Navigate to="/cadastro" replace />,
      },
      {
        path: "/checklist",
        element: <ProtectedRoute><ChecklistModule /></ProtectedRoute>,
      },
      {
        path: "/dashboard-config",
        element: <ProtectedRoute requireAdmin><DashboardConfig /></ProtectedRoute>,
      },
      {
        path: "/document-forms",
        element: <ProtectedRoute requireAdmin><DocumentForms /></ProtectedRoute>,
      },
      {
        path: "/works",
        element: <ProtectedRoute><Works /></ProtectedRoute>,
      },
      {
        path: "/templates",
        element: <ProtectedRoute requireAdmin><Templates /></ProtectedRoute>,
      },
      {
        path: "/inspections",
        element: <ProtectedRoute><Inspections /></ProtectedRoute>,
      },
      {
        path: "/inspections/new",
        element: <ProtectedRoute><NewInspection /></ProtectedRoute>,
      },
      {
        path: "/inspections/:id/edit",
        element: <ProtectedRoute><EditInspection /></ProtectedRoute>,
      },
      {
        path: "/inspections/:id",
        element: <ProtectedRoute><InspectionDetail /></ProtectedRoute>,
      },
      {
        path: "/tenants",
        element: <ProtectedRoute requireSuperAdmin><Tenants /></ProtectedRoute>,
      },
      {
        path: "/users",
        element: <ProtectedRoute requireAdmin><Users /></ProtectedRoute>,
      },
      {
        path: "/audit-logs",
        element: <ProtectedRoute requireSuperAdmin><AuditLogs /></ProtectedRoute>,
      },
      {
        path: "/feedback",
        element: <ProtectedRoute><Feedback /></ProtectedRoute>,
      },
      {
        path: "/feedback-list",
        element: <ProtectedRoute requireSuperAdmin><FeedbackList /></ProtectedRoute>,
      },
      {
        path: "/workforce",
        element: <ProtectedRoute><Workforce /></ProtectedRoute>,
      },
      {
        path: "/permissions",
        element: <ProtectedRoute requireAdmin><Permissions /></ProtectedRoute>,
      },
      {
        path: "/sectors",
        element: <ProtectedRoute><Sectors /></ProtectedRoute>,
      },
      {
        path: "/fvm-materials",
        element: <ProtectedRoute requireAdmin><FvmMaterials /></ProtectedRoute>,
      },
      {
        path: "/diario",
        element: <ProtectedRoute><SiteDiary /></ProtectedRoute>,
      },
      {
        path: "/diario/fotos",
        element: <ProtectedRoute><SiteDiaryPhotos /></ProtectedRoute>,
      },
      {
        path: "/cronograma",
        element: <ProtectedRoute><Schedule /></ProtectedRoute>,
      },
      {
        path: "/projetos",
        element: <ProtectedRoute><Projects /></ProtectedRoute>,
      },
      {
        path: "/projetos/lista-mestra",
        element: <ProtectedRoute><MasterList /></ProtectedRoute>,
      },
      {
        path: "/trainings",
        element: <ProtectedRoute><Trainings /></ProtectedRoute>,
      },
      {
        path: "/equipamentos",
        element: <ProtectedRoute><Equipment /></ProtectedRoute>,
      },
      {
        path: "/planejamento",
        element: <ProtectedRoute><Planejamento /></ProtectedRoute>,
      },
      {
        path: "/planejamento/empreendimento",
        element: <ProtectedRoute><Empreendimento /></ProtectedRoute>,
      },
      {
        path: "/planejamento/estrutura-da-obra",
        element: <ProtectedRoute><WorkStructure /></ProtectedRoute>,
      },
      {
        path: "/estoque",
        element: <ProtectedRoute><Stock /></ProtectedRoute>,
      },
      {
        path: "/compras",
        element: <ProtectedRoute><Purchases /></ProtectedRoute>,
      },
      {
        path: "/canais-ia",
        element: <ProtectedRoute><Chatbots /></ProtectedRoute>,
      },
      {
        path: "/canais-ia/:id",
        element: <ProtectedRoute><ChatbotDetail /></ProtectedRoute>,
      },
      {
        path: "/traceability",
        element: <ProtectedRoute><Traceability /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete",
        element: <ProtectedRoute><ConcreteIndex /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/suppliers",
        element: <ProtectedRoute><ConcreteSuppliers /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/mixes",
        element: <ProtectedRoute><ConcreteMixes /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/elements",
        element: <ProtectedRoute><StructuralElements /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/labs",
        element: <ProtectedRoute><ConcreteLabs /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/pours",
        element: <ProtectedRoute><ConcretePoursList /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/pours/new",
        element: <ProtectedRoute><ConcretePourForm /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/pours/:id/edit",
        element: <ProtectedRoute><ConcretePourForm /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/specimens",
        element: <ProtectedRoute><ConcreteSpecimens /></ProtectedRoute>,
      },
      {
        path: "/traceability/concrete/dashboard",
        element: <ProtectedRoute><ConcreteDashboard /></ProtectedRoute>,
      },
      {
        path: "/traceability/thickness",
        element: <ProtectedRoute><ThicknessDashboard /></ProtectedRoute>,
      },
      {
        path: "/traceability/thickness/services",
        element: <ProtectedRoute><ThicknessServices /></ProtectedRoute>,
      },
      {
        path: "/traceability/thickness/new",
        element: <ProtectedRoute><ThicknessEntry /></ProtectedRoute>,
      },
      {
        path: "/traceability/thickness/:id/edit",
        element: <ProtectedRoute><ThicknessEntry /></ProtectedRoute>,
      },

      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <RouterProvider router={router} />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
